import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ChipFilterValue } from '@openx/types/chipFilters.ts';
import type { OptionsMap } from '@openx/types/options.ts';
import { XIcon } from '@phosphor-icons/react/X';
import some from 'lodash/some';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AutoComplete } from '../../../AutoComplete/AutoComplete.tsx';
import { Button } from '../../../Button/Button.tsx';

import { ListChipActiveFilters } from './ListChipActiveFilters.tsx';
import { StyledBox, StyledInputBox } from './ListChipFilters.styled.ts';

export type ListChipFilterConfig = {
  displayName: string;
  isTextInput?: boolean;
  isLoading?: boolean;
  options?: OptionsMap;
};

export type ListChipFiltersProps<TFilters extends Record<string, ChipFilterValue>> = {
  anchor: HTMLElement | null;
  filters: TFilters;
  filtersConfig: Record<keyof TFilters, ListChipFilterConfig>;
  isDisabled?: boolean;
  onChange: (newFilters: TFilters) => void;
  setAnchor: (anchor: HTMLElement | null) => void;
  dataTest?: string;
};

export const ListChipFilters = <TFilters extends Record<string, ChipFilterValue>>({
  anchor,
  filters,
  filtersConfig,
  isDisabled = false,
  onChange,
  setAnchor,
  dataTest,
}: ListChipFiltersProps<TFilters>) => {
  const [selectedFilterName, setSelectedFilterName] = useState<string>('');
  const [selectedFilterValue, setSelectedFilterValue] = useState<string>('');
  const { t } = useTranslation();

  const hasTextInputValue = filtersConfig[selectedFilterName]?.isTextInput;
  const isOpen = Boolean(anchor) && !isDisabled;

  const filterNames = useMemo<{ active: string[]; inactive: string[] }>(
    () =>
      Object.keys(filtersConfig).reduce<{ active: string[]; inactive: string[] }>(
        (acc, filterName) => {
          if (filters[filterName] !== null) {
            acc.active.push(filterName);
          } else {
            acc.inactive.push(filterName);
          }

          return acc;
        },
        {
          active: [],
          inactive: [],
        }
      ),
    [filters, filtersConfig]
  );

  const filterOptions = useMemo<string[]>(
    () => Object.keys(filtersConfig[selectedFilterName]?.options || {}),
    [filtersConfig, selectedFilterName]
  );

  const hasAnyFilterAdded = useMemo<boolean>(() => some(filters, value => value !== null), [filters]);

  const handleApply = () => {
    const newFilters: Record<string, ChipFilterValue> = { ...filters };
    newFilters[selectedFilterName] = selectedFilterValue;

    onChange(newFilters as TFilters);
    setSelectedFilterName('');
    setSelectedFilterValue('');
  };

  const handleChipDelete = (filterName: string) => {
    const newFilters: Record<string, ChipFilterValue> = { ...filters };
    newFilters[filterName] = null;

    onChange(newFilters as TFilters);
  };

  const handleClose = () => {
    setAnchor(null);
    setSelectedFilterName('');
    setSelectedFilterValue('');
  };

  const handleClear = () => {
    const newFilters: Record<string, ChipFilterValue> = { ...filters };
    for (const filterName in newFilters) {
      newFilters[filterName] = null;
    }

    onChange(newFilters as TFilters);
    setSelectedFilterName('');
    setSelectedFilterValue('');
  };

  return (
    <Popover
      anchorEl={anchor}
      anchorOrigin={{
        horizontal: 'right',
        vertical: 'bottom',
      }}
      onClose={handleClose}
      open={isOpen}
      transformOrigin={{
        horizontal: 'right',
        vertical: 'top',
      }}
      slotProps={{
        paper: {
          ...({
            'data-test': dataTest ?? 'filters-dialog',
          } as React.HTMLAttributes<HTMLDivElement>),
        },
      }}
    >
      <StyledBox>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography gutterBottom variant="h3">
            {t('Filters')}
          </Typography>
          <IconButton aria-label="Close" onClick={handleClose} size="large">
            <XIcon data-test="close-dialog" />
          </IconButton>
        </Stack>

        <Divider />

        <ListChipActiveFilters
          activeFilters={filterNames.active}
          filters={filters}
          filtersConfig={filtersConfig}
          onDelete={handleChipDelete}
        />

        {filterNames.active.length !== 0 && <Divider />}

        <StyledInputBox>
          <AutoComplete
            disableClearable
            fullWidth
            getOptionLabel={name => filtersConfig[name]?.displayName || name}
            onChange={(_, value) => setSelectedFilterName(value)}
            options={filterNames.inactive}
            textFieldProps={{
              'data-test': 'filter-type-input',
              label: t('Info*'),
              margin: 'dense',
              placeholder: t('[ Select Info ]'),
            }}
            value={selectedFilterName}
          />

          <AutoComplete
            autoSelect={hasTextInputValue}
            disabled={!selectedFilterName}
            disableClearable
            freeSolo={hasTextInputValue}
            fullWidth
            getOptionLabel={value => filtersConfig[selectedFilterName]?.options?.[value]?.name || value}
            onChange={(_, value) => setSelectedFilterValue(value)}
            onInputChange={(_, value) => hasTextInputValue && setSelectedFilterValue(value)}
            options={filterOptions}
            textFieldProps={{
              'data-test': 'filter-value-input',
              label: t('Value'),
              margin: 'dense',
              placeholder: t('[ Select or Search ]'),
            }}
            value={selectedFilterValue}
          />
        </StyledInputBox>

        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-end',
          }}
        >
          <Button
            color="error"
            data-test="clear-button"
            disabled={!hasAnyFilterAdded}
            onClick={handleClear}
            variant="text"
          >
            {t('Clear')}
          </Button>

          <Button
            data-test="apply-button"
            color="primary"
            disabled={!selectedFilterValue}
            onClick={handleApply}
            variant="text"
          >
            {t('Apply')}
          </Button>
        </Stack>
      </StyledBox>
    </Popover>
  );
};
